<script lang="ts">
import {computed, defineComponent, Ref, ref, watch} from '@vue/composition-api'

import registrationsApi from '@/api/registrations.api'
import RegistrationStatusCard from '@/components/registrations/HondaRegistration/detail/RegistrationStatusCard.vue'
import {RegistrationDoc} from '@/types'
import {Product} from '@/types/dynamodb-types'
import {mapGetters} from '@/utils/map-store'
import {generateUUID} from '@/utils/utils'

export default defineComponent({
  name: 'FinishDetail',
  components: {RegistrationStatusCard},
  props: {
    step: {
      type: Number,
      required: true,
    },
  },
  setup(props, {root}) {
    const loading = ref(false)
    const registration = ref(null) as Ref<RegistrationDoc | null>
    const {getId, getGroup: group, getCurrentStep: currentStep, getServices: services} = mapGetters(root.$store.getters)

    const hasHondaMapit = computed(() => {
      return registration.value?.products.includes(Product.HondaMapit)
    })
    const hasHondaPlusOrInsurance = computed(() => {
      return (
        registration.value?.products.includes(Product.HondaPlus) ||
        registration.value?.products.includes(Product.Insurance) ||
        registration.value?.products.includes(Product.HondaPlusGo)
      )
    })

    const hasComms = computed(() => {
      return (
        hasHondaMapit.value &&
        registration.value?.deviceHealthChecked !== undefined &&
        registration.value?.deviceHealthChecked
      )
    })

    const willHaveHondaMapit = computed(() => {
      return services.value?.['honda-mapit'].selected
    })

    const willHaveHondaPlus = computed(() => {
      return (
        services.value?.['honda-plus'].selected ||
        services.value?.['honda-plus-go'].selected ||
        services.value?.['honda-seguro-normal'].selected ||
        services.value?.['honda-seguro-plus'].selected
      )
    })

    const willHaveComms = computed(() => {
      return willHaveHondaMapit.value
    })

    const statusChecks = computed(() => {
      return loading.value
        ? []
        : [
            {
              show: hasHondaPlusOrInsurance.value,
              valid: registration.value?.contractsSigned,
              name: 'HP',
              icon: 'fa-solid fa-signature',
            },
            {show: hasHondaMapit.value, valid: registration.value?.accountRegistrationCompleted, name: 'HM'},
            {show: hasHondaMapit.value, valid: hasComms.value, name: 'COMMS', icon: 'fa-solid fa-wifi'},
          ].filter(item => item.show)
    })

    const placeholderChecks = computed(() => {
      return loading.value
        ? [willHaveHondaPlus.value, willHaveHondaMapit.value, willHaveComms.value].filter(check => check)
        : []
    })

    const buttonStyle = computed(() => {
      return `
        text-transform: uppercase !important;
        padding: 8px 24px !important;
        font-family: 'ProximaNovaExCnSbold', sans-serif !important;
        letter-spacing: 0.01em !important;
      `
    })

    watch(currentStep, val => {
      if (val === 7) getRegistration()
    })

    async function goToRegistrations() {
      if (group.value && group.value.dealerId) root.$store.dispatch('setPendingRegistrations', group.value.dealerId)

      await root.$router.push('/registrations')
      root.$store.dispatch('resetRegistration')
    }

    function getRegistration() {
      if (!loading.value && getId.value) {
        loading.value = true
        registrationsApi
          .getRegistration(group.value.dealerId, getId.value)
          .then(({data}) => {
            registration.value = data
          })
          .catch(e => {
            console.log('Error get registration: ', e)
          })
          .finally(() => {
            loading.value = false
          })
      }
    }

    return {
      loading,
      registration,
      getId,
      group,
      currentStep,
      hasHondaMapit,
      hasHondaPlusOrInsurance,
      hasComms,
      statusChecks,
      buttonStyle,
      willHaveHondaMapit,
      willHaveHondaPlus,
      willHaveComms,
      placeholderChecks,
      generateUUID,
      goToRegistrations,
      getRegistration,
    }
  },
})
</script>

<template>
  <div id="finishDetail">
    <v-stepper-content :step="step" class="py-0 px-10">
      <div class="finish-detail-wrapper">
        <h1>{{ $t('honda.registration.finish.title') }}</h1>
        <p class="subtitle" v-html="$t('honda.registration.finish.subtitle')" />
        <div class="finish-detail-grid">
          <registration-status-card v-for="{valid, name, icon} in statusChecks" :key="`status-${name}`" :valid="valid">
            <template #icon>
              <img
                v-if="name === 'HM'"
                :src="`/images/registrations/hm-logo-${valid ? 'secondary' : 'primary'}.svg`"
                alt="Honda Mapit" />
              <v-icon v-else :size="32" :color="valid ? '#00803E' : '#DC182D'">
                {{ icon }}
              </v-icon>
            </template>
            <template #title>
              {{ $t(`honda.registration.finish.${name}.title`) }}
            </template>
            <template #description>
              <p v-html="$t(`honda.registration.finish.${name}.description.${valid ? 'valid' : 'invalid'}`)" />
            </template>
          </registration-status-card>
          <registration-status-card :placeholder="true" v-for="(_, i) in placeholderChecks" :key="i">
            <template #icon>
              <v-skeleton-loader type="avatar" />
            </template>
            <template #title>
              <v-skeleton-loader type="heading" class="header-skeleton" :width="200" />
            </template>
            <template #description>
              <v-skeleton-loader type="paragraph" style="width: 100%" />
            </template>
          </registration-status-card>
        </div>
        <div class="footer-actions">
          <m-btn
            id="refresh"
            color="secondary"
            class="no-shadow registration-action-button"
            @click="getRegistration()"
            :style="buttonStyle">
            <v-icon color="white" :size="16">fa-solid fa-refresh</v-icon>
            <span class="ml-2">{{ $t('honda.registration.finish.refresh') }}</span>
          </m-btn>
          <m-btn
            id="finish"
            color="primary"
            class="no-shadow registration-action-button"
            @click="goToRegistrations()"
            :style="buttonStyle">
            {{ $t('honda.registration.finish.finish') }}
          </m-btn>
        </div>
      </div>
    </v-stepper-content>
  </div>
</template>

<style lang="scss">
.header-skeleton {
  .v-skeleton-loader__heading {
    width: 75% !important;
  }
}
</style>
<style lang="scss" scoped>
.finish-detail-wrapper {
  border: 1px solid #bbbbbb;
  border-radius: 8px;
  padding: 56px 56px 30px 56px;
  box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
  margin-bottom: 3rem;
  margin-left: 8px;
  margin-right: 8px;

  h1,
  p {
    margin: 0;
  }

  > h1 {
    font-family: 'ProximaNovaExCnSbold', sans-serif;
    font-weight: 600;
    font-size: 32px;
    letter-spacing: -0.01em;
    color: #dc182d;
    margin-bottom: 6px;
    text-transform: uppercase;
    text-align: center;
  }

  .subtitle {
    font-size: 16px;
    color: #222;
    margin-bottom: 4rem;
    text-align: center;
  }

  .finish-detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, 360px);
    grid-gap: 20px;
    justify-content: center;
    margin-top: 20px;
    width: 100%;
  }
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6rem;

  button {
    margin-left: 1rem;
  }
}

.fade-in {
  &-leave-active,
  &-enter-active {
    transition: opacity 0.1s linear;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>
